import {
  Paintbrush2,
  Bolt,
  Package,
  ScanSearch,
  Shirt,
  Swords,
  FileText,
  Sparkles,
  Image as ImageIcon,
  Award,
  Brush,
  UserPlus,
  Scissors,
  ImageDown,
  AlertTriangle,
  Workflow,
  BookOpenText,
  Box,
} from "lucide-react";
import { Callout, CodeBlock, Tag } from "@/components/GuidesShared";
import { StepList, BulletList } from "@/lib/guides/primitives";
import type { GuideSectionDef } from "@/lib/guides/types";

export const toolsSections: GuideSectionDef[] = [
  {
    id: "paint-booth",
    label: "Paint Booth",
    icon: <Paintbrush2 className="h-5 w-5" />,
    shortDescription: "Real-time 3D livery and texture preview in the browser.",
    seoTitle: "Paint Booth — Real-Time 3D Livery Preview for FiveM",
    seoDescription:
      "Preview FiveM vehicle liveries and textures live on a 3D model in your browser. Drag in DDS, PNG, or YTD files, adjust lighting, and screenshot — no in-game reload.",
    keywords: [
      "FiveM livery preview",
      "3D vehicle livery viewer",
      "GTA5 texture preview",
      "DDS livery preview",
      "FiveM paint booth",
    ],
    toolHref: "/paintbooth",
    faqs: [
      {
        q: "What file types can I preview in Paint Booth?",
        a: "Textures as PNG, DDS, or YTD, dropped onto the model. Subscribers can also upload their own 3D models in .glb, .obj, or .yft; demo mode uses a fixed model set.",
      },
      {
        q: "Why are my YFT textures missing in Paint Booth?",
        a: "Re-export the YFT from OpenIV with 'Embed Textures' checked. Paint Booth reads embedded textures from the YFT, so if they weren't embedded on export the model loads untextured.",
      },
      {
        q: "Do I need the game open to preview a livery?",
        a: "No — Paint Booth renders the livery on a 3D model entirely in the browser, so you can iterate and screenshot without an in-game reload.",
      },
      {
        q: "Can I export a preview image?",
        a: "Yes. Adjust the environment preset, exposure, and shadow intensity, then click Export to download a high-resolution PNG screenshot.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Paint Booth is a real-time 3D model viewer that lets you upload custom textures, swap
          livery layers, adjust environment lighting, and screenshot the result — all in the
          browser.
        </p>
        <Callout type="info">
          Demo mode uses a locked model set. Subscribers can upload their own{" "}
          <Tag>.glb</Tag>, <Tag>.obj</Tag>, or <Tag>.yft</Tag> models.
        </Callout>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Open Paint Booth and select or upload a 3D model.",
            "Drag a texture file (PNG, DDS, or YTD) onto the drop zone, or use the texture picker.",
            "Adjust the environment preset, exposure, and shadow intensity with the side controls.",
            "Use the color channel mixer to override livery layers.",
            "Click Export to download a high-res PNG screenshot.",
          ]}
        />
        <Callout type="tip">
          YFT files from OpenIV export correctly when exported with{" "}
          <strong>textures embedded</strong>. If textures appear missing, re-export from OpenIV
          with "Embed Textures" checked.
        </Callout>
      </>
    ),
  },
  {
    id: "eup-viewer",
    label: "EUP Viewer",
    icon: <Shirt className="h-5 w-5" />,
    shortDescription: "Inspect EUP clothing drawables (.ydd) before deploying them.",
    seoTitle: "EUP Viewer — Inspect FiveM EUP Clothing (.ydd) Online",
    seoDescription:
      "Upload EUP clothing drawables (.ydd) and inspect components, texture links, and embedded flags before deploying to your FiveM server. Browser-based, no install.",
    keywords: [
      "EUP viewer",
      "FiveM EUP clothing",
      "ydd viewer",
      "EUP drawable inspector",
      "Emergency Uniform Pack",
    ],
    toolHref: "/eup-viewer",
    faqs: [
      {
        q: "What can EUP Viewer inspect in a .ydd file?",
        a: "The component list and drawable indices, which YTD texture dictionary the model links to, embedded flags (male/female and component type), and the file signature and format version.",
      },
      {
        q: "Do I need to upload the YTD as well?",
        a: "No — upload the YDD by itself. If it references an external YTD you'll see an expected texture-missing warning; the inspection of components and metadata still works.",
      },
      {
        q: "Does EUP Viewer show the component slot?",
        a: "Yes. It reads the component ID embedded in each YDD automatically (for example 3 for upper body, 11 for a jacket), so you don't have to look it up by hand.",
      },
      {
        q: "Can I check an EUP pack before deploying it to my server?",
        a: "That's exactly what it's for — verify components, texture linkage, and file integrity in the browser before streaming the clothing to your FiveM server.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          EUP Viewer parses YDD drawable archives used by Emergency Uniform Pack and similar
          clothing mods. Upload a <Tag>.ydd</Tag> file to inspect its components, read the
          embedded metadata, and verify file integrity before deploying to your server.
        </p>
        <p className="text-sm font-medium text-foreground">What you can inspect</p>
        <BulletList
          items={[
            "Component list and drawable indices",
            "Texture dictionary linkage (which YTD the model references)",
            "Embedded flags (male/female, component type)",
            "File signature and format version",
          ]}
        />
        <Callout type="warn">
          YDD files that reference external YTD archives will show a texture-missing warning.
          This is expected — you only need to upload the YDD itself.
        </Callout>
      </>
    ),
  },
  {
    id: "blinklab",
    label: "Blinklab",
    icon: <Bolt className="h-5 w-5" />,
    shortDescription: "Build siren and lightbar patterns on an 8-color grid.",
    seoTitle: "Blink Lab — FiveM Siren & Lightbar Pattern Editor",
    seoDescription:
      "Build ELS and carcols.meta siren patterns on an 8-color grid, preview them live, and export binary or decimal sequences for your FiveM lightbar setup.",
    keywords: [
      "FiveM siren pattern editor",
      "carcols.meta generator",
      "lightbar pattern",
      "ELS pattern editor",
      "siren sequence",
    ],
    toolHref: "/blinklab",
    faqs: [
      {
        q: "What does Blink Lab export?",
        a: "Binary sequences for raw ELS configs, or decimal sequences for carcols.meta. The Carcols sub-tool at /blinklab/carcols generates a full carcols.meta XML block ready to paste into your resource.",
      },
      {
        q: "How do I control flash speed?",
        a: "Set the BPM — it drives how fast the sequence steps through frames. You can watch the live animation in the Preview panel before exporting.",
      },
      {
        q: "How many light slots does Blink Lab support?",
        a: "Eight colour slots. Pick a colour per slot (or leave it off) and toggle slots on and off per frame to build the pattern.",
      },
      {
        q: "Can Blink Lab make a carcols.meta for me?",
        a: "Yes — the /blinklab/carcols sub-tool turns your pattern into a complete carcols.meta siren block, so you don't have to hand-write the XML.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Blinklab is a visual lightbar pattern editor. Build flash sequences on an 8-slot color
          grid, preview them live, and export as binary or decimal sequences for use in your ELS
          or carcols configuration.
        </p>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Pick a color for each of the 8 light slots (or leave them off).",
            "Set the BPM to control flash speed.",
            "Toggle individual slots on/off per frame to build the sequence.",
            "Use the Preview panel to see the live animation.",
            "Export as Binary (for raw ELS configs) or Decimal (for carcols.meta).",
          ]}
        />
        <p className="text-sm leading-7 text-muted-foreground">
          The Carcols sub-tool at{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">/blinklab/carcols</code>{" "}
          generates a full <Tag>carcols.meta</Tag> XML block from your pattern, ready to paste
          into your resource.
        </p>
      </>
    ),
  },
  {
    id: "blinklab-3d",
    label: "3D BlinkLab",
    icon: <Box className="h-5 w-5" />,
    shortDescription: "Design a siren pattern and watch it play on your own vehicle model in 3D.",
    seoTitle: "3D BlinkLab — Preview FiveM Siren Patterns on Your Vehicle",
    seoDescription:
      "Load your car's .yft, paint a 32-siren pattern, and watch it flash on the model in real time. Export a ready-to-paste carcols.meta for your FiveM resource.",
    keywords: [
      "FiveM siren preview",
      "3D siren pattern editor",
      "carcols.meta siren",
      "lightbar 3D preview",
      "GTA5 emergency lighting",
    ],
    toolHref: "/blinklab-3d",
    faqs: [
      {
        q: "Which files do I need?",
        a: "The car's .yft is required. Select its .ytd textures at the same time so the model isn't untextured, and add its carcols.meta if you want to bring in an existing pattern. Liveries load separately as PNG, JPG, DDS, or YTD.",
      },
      {
        q: "Why does it say my car has no lights it can use?",
        a: "3D BlinkLab drives each siren from a bone named Light1, Light2, and so on, inside a part bone. A model without those bones has nothing to flash. Load the regular .yft, not the _hi.yft.",
      },
      {
        q: "What does it export?",
        a: "A carcols.meta. Create carcols asks for the model name, a numeric carcols ID between 10000 and 99999, and the scale factor, then saves the file or copies either the siren block or the whole file.",
      },
      {
        q: "How many sirens can a pattern have?",
        a: "32 — one per grid column, with 32 steps per pattern. If you import a carcols.meta with more sirens, only the first 32 are kept.",
      },
      {
        q: "Who can use 3D BlinkLab?",
        a: "3D BlinkLab is included with the Vehicle Developer plan and above, which means Vehicle Developer, Pro, and Studio. The 2D Blink Lab pattern editor is part of the same plans.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          3D BlinkLab is Blink Lab with your vehicle in the room. Paint a siren pattern on the
          grid and it plays on the actual car model as you work, so you can check direction,
          color and timing before anything reaches your server. It is included with the Vehicle
          Developer, Pro, and Studio plans.
        </p>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Click Load your car and select the car's .yft together with its .ytd textures. Add the carcols.meta too if you want to start from an existing pattern.",
            "If you imported a carcols.meta, choose Full pattern to bring in everything, or Siren rotations only to keep the pattern you already have.",
            "Pick one of the six colors (or press 1–6) and drag across the grid. Each column drives one siren and each row is one step. Right-click erases.",
            "Click a column to open Selected Column(s), where you can set its rotation in degrees, recolor it, copy it, or reset it.",
            "Press play and set the BPM to control speed. Drag the preview to turn the car, scroll to zoom, and double-click to reset the view.",
            "Fix any red notes in the status area, then click Create carcols to save or copy your carcols.meta.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Saving your work</p>
        <BulletList
          items={[
            "Save to My Projects stores the full editable project and its vehicle files in your Workshop account. Open project brings it back.",
            "Export project ZIP and Import project ZIP move a complete project between machines as a .blinklab.zip.",
            "Save selection as pattern keeps a set of columns you can reuse in later projects.",
          ]}
        />
        <Callout type="tip">
          Want the preview in its own window? Use the pop-out button on the Vehicle preview card.
          It needs Chrome or Edge with pop-ups allowed for The Workshop.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Only working on the timing? The 2D editor at{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">/blinklab</code> builds
          the same kind of pattern without loading a vehicle.
        </p>
      </>
    ),
  },
  {
    id: "uv-studio",
    label: "UV Studio",
    icon: <Paintbrush2 className="h-5 w-5" />,
    shortDescription: "Recover and export UV templates from already-mapped models.",
    seoTitle: "UV Studio — Recover & Export UV Templates for FiveM",
    seoDescription:
      "Render a clean UV unwrap template from any mapped 3D model for paint-over guides in Photoshop. Normal shading, wireframe overlay, transparent PNG up to 4K.",
    keywords: [
      "UV template export",
      "FiveM UV map",
      "vehicle UV template",
      "UV unwrap export",
      "livery template",
    ],
    toolHref: "/uv-studio",
    faqs: [
      {
        q: "What does UV Studio produce?",
        a: "A UV unwrap template rendered directly from your 3D model — a clean paint-over guide you can open in Photoshop to design a livery that lines up with the model's surface.",
      },
      {
        q: "What resolution can I export?",
        a: "1024 px, 2048 px, or 4096 px. Use a power-of-two size so the resulting textures render correctly in-game.",
      },
      {
        q: "Can I get a transparent template for layering?",
        a: "Yes — export a PNG with a transparent background so the template drops straight onto layers in Photoshop over your artwork.",
      },
      {
        q: "What do the normal-color and wireframe options do?",
        a: "Normal-color shading tints each polygon by its surface direction so you can read orientation at a glance; the wireframe overlay draws the UV edge lines on top so seams and islands are easy to follow.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          UV Studio renders a UV unwrap template directly from your loaded 3D model. Use it to
          create clean paint-over guides for Photoshop or other image editors.
        </p>
        <p className="text-sm font-medium text-foreground">Export options</p>
        <BulletList
          items={[
            "Normal-color shading — each polygon tinted by its surface normal for easy orientation.",
            "Wireframe overlay — draw UV edge lines on top of the normal render.",
            "Transparent background — export a PNG with no background for layering in Photoshop.",
            "Resolution selector — 1024 px, 2048 px, or 4096 px output.",
          ]}
        />
      </>
    ),
  },
  {
    id: "weapon-designer",
    label: "Weapon Designer",
    icon: <Swords className="h-5 w-5" />,
    shortDescription: "Iterate on weapon textures with a 3D viewer.",
    seoTitle: "Weapon Designer — Preview FiveM Weapon Textures in 3D",
    seoDescription:
      "Load GTA5 weapon drawables (.ydr), swap textures, and inspect every angle in a browser 3D viewer — iterate on weapon skins without an in-game reload loop.",
    keywords: [
      "FiveM weapon texture preview",
      "ydr viewer",
      "weapon skin preview",
      "GTA5 weapon designer",
      "weapon retexture",
    ],
    toolHref: "/weapon-viewer",
    faqs: [
      {
        q: "What files does Weapon Designer load?",
        a: "GTA5 .ydr weapon drawables, exported from OpenIV or a compatible tool. You can optionally add a matching .ytd to load custom textures; both embedded and external texture dictionaries are supported.",
      },
      {
        q: "Can I preview a weapon skin without loading into the game?",
        a: "Yes — rotate, zoom, and pan the model in the browser 3D viewer and swap texture channels to iterate on a skin, with no in-game reload loop.",
      },
      {
        q: "How do I swap individual textures?",
        a: "Use the texture slot picker to replace individual texture channels on the loaded weapon and see the change immediately in the viewer.",
      },
      {
        q: "Is it the same engine as Paint Booth?",
        a: "Yes — Weapon Designer shares Paint Booth's 3D engine, so lighting and model handling behave the same way.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Weapon Designer lets you load GTA5 YDR weapon drawables, apply replacement textures,
          and inspect model details in the browser. It shares the same 3D engine as Paint Booth.
        </p>
        <Callout type="info">
          YDR files must be exported from OpenIV or a compatible tool. The viewer supports both
          embedded and external <Tag>.ytd</Tag> texture dictionaries.
        </Callout>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Upload a .ydr weapon drawable file.",
            "Optionally upload a matching .ytd to load custom textures.",
            "Rotate, zoom, and pan the model to inspect all angles.",
            "Use the texture slot picker to swap individual texture channels.",
          ]}
        />
      </>
    ),
  },
  {
    id: "easy-meta",
    label: "Easy Meta",
    icon: <FileText className="h-5 w-5" />,
    shortDescription: "Generate vehicles.meta, handling, carcols, and carvariations from a guided UI.",
    seoTitle: "Easy Meta — Generate FiveM vehicles.meta & handling.meta",
    seoDescription:
      "Generate valid vehicles.meta, handling.meta, carvariations.meta, and carcols.meta for FiveM vehicles from a guided form — no hand-editing XML.",
    keywords: [
      "vehicles.meta generator",
      "handling.meta editor",
      "FiveM vehicle meta",
      "carvariations generator",
      "carcols.meta",
    ],
    toolHref: "/easy-meta",
    faqs: [
      {
        q: "Which files does Easy Meta generate?",
        a: "All four core vehicle meta files — vehicles.meta, handling.meta, carvariations.meta, and carcols.meta — as valid XML, from a guided form.",
      },
      {
        q: "Do I need to know XML to use Easy Meta?",
        a: "No. You fill in form fields and export valid meta files without writing a single line of XML by hand.",
      },
      {
        q: "How do I get realistic handling values?",
        a: "Start from a known-good vanilla GTA5 handling entry (for example POLICE) to get sensible baselines for mass, drive force, and traction, then adjust incrementally.",
      },
      {
        q: "Does Easy Meta handle kit IDs and siren wiring?",
        a: "Yes — it assigns kit IDs and wires the carcols and carvariations references automatically, which prevents the modkit ID conflicts that make vehicles share liveries or parts.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Easy Meta is a guided editor for the four core vehicle meta files required by every
          FiveM vehicle resource. Fill in the form fields and export valid XML files without
          writing a single line by hand.
        </p>
        <p className="text-sm font-medium text-foreground">Generated files</p>
        <ul className="space-y-2">
          {[
            { tag: "vehicles.meta", desc: "Model name, class, flags, layout, and audio hash." },
            { tag: "handling.meta", desc: "Mass, drive force, traction, and suspension values." },
            { tag: "carvariations.meta", desc: "Plate colors, wheel liveries, and extras." },
            { tag: "carcols.meta", desc: "Siren colors, light patterns, and kit entries." },
          ].map(({ tag, desc }) => (
            <li key={tag} className="flex items-start gap-2.5 text-sm text-muted-foreground">
              <Tag>{tag}</Tag>
              <span className="flex-1">{desc}</span>
            </li>
          ))}
        </ul>
        <Callout type="tip">
          Start from a vanilla GTA5 handling entry (e.g. <strong>POLICE</strong>) to get
          realistic baselines, then tweak from there.
        </Callout>
      </>
    ),
  },
  {
    id: "packbench",
    label: "PackBench",
    icon: <Package className="h-5 w-5" />,
    shortDescription: "Bundle models, textures, and metas into a streaming-ready resource zip.",
    seoTitle: "PackBench — Build Streaming-Ready FiveM Resource Zips",
    seoDescription:
      "Drop your YFT, YTD, and meta files into PackBench to auto-generate the folder layout and fxmanifest.lua, then download a clean, streaming-ready FiveM resource.",
    keywords: [
      "FiveM resource builder",
      "fxmanifest generator",
      "stream folder structure",
      "FiveM resource zip",
      "vehicle resource packer",
    ],
    toolHref: "/pack-bench",
    faqs: [
      {
        q: "What does PackBench build?",
        a: "A correctly-structured, streaming-ready FiveM resource ZIP — it lays out the stream/ folder, writes fxmanifest.lua with the right data_file entries, and zips everything ready to deploy.",
      },
      {
        q: "Does PackBench write fxmanifest.lua for me?",
        a: "Yes. It generates fxmanifest.lua, including the data_file lines for your meta files and the files{} block for the streamed assets, so nothing is missed.",
      },
      {
        q: "What files do I drop in?",
        a: "Your raw assets — the .yft (and optional _hi.yft high-LOD), .ytd, and the vehicle .meta files. PackBench arranges them into the correct folder structure.",
      },
      {
        q: "Can I use it with Easy Meta output?",
        a: "Yes — run PackBench after Easy Meta and import the meta files directly from the Easy Meta export to bundle a complete resource.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          PackBench assembles a correctly-structured FiveM resource ZIP from raw asset files.
          Drop your YFT, YTD, and meta files onto the builder — it generates the folder layout,
          writes <Tag>fxmanifest.lua</Tag>, and zips everything ready to deploy.
        </p>
        <p className="text-sm font-medium text-foreground">Output structure</p>
        <CodeBlock>{`stream/
  your_vehicle.yft
  your_vehicle_hi.yft
  your_vehicle.ytd
  vehicles.meta
  handling.meta
  carvariations.meta
  carcols.meta
fxmanifest.lua`}</CodeBlock>
        <Callout type="tip">
          Use PackBench <em>after</em> Easy Meta — you can import the meta files directly from
          the Easy Meta export.
        </Callout>
      </>
    ),
  },
  {
    id: "code-smith",
    label: "Code Smith",
    icon: <ScanSearch className="h-5 w-5" />,
    shortDescription: "Scan FiveM resources for loop abuse, missing waits, and asset clutter.",
    seoTitle: "Code Smith — Scan FiveM Resources for Performance Issues",
    seoDescription:
      "Scan a zipped FiveM resource for loop abuse, missing Wait() calls, heavy event handlers, and asset clutter. Get a per-file report to fix server lag.",
    keywords: [
      "FiveM resource scanner",
      "Lua performance scan",
      "missing Wait detection",
      "FiveM server optimization",
      "resource lag",
    ],
    toolHref: "/code-smith",
    faqs: [
      {
        q: "What issues does Code Smith detect?",
        a: "Performance anti-patterns in a zipped resource — tight loops with no Wait() call, heavy per-tick event handlers, missing CreateThread wrappers, unused asset references, and deprecated natives.",
      },
      {
        q: "Will Code Smith find every bug in my resource?",
        a: "No — it's a static analyser, so it catches known anti-patterns, not runtime bugs or logic errors. Use it to clear common performance problems, then test in-game.",
      },
      {
        q: "What do I upload to Code Smith?",
        a: "A ZIP of your FiveM resource folder with fxmanifest.lua at the root. Drop it in and each finding shows the file, line, and a description.",
      },
      {
        q: "How does it help server performance?",
        a: "The most common cause of server thread starvation is a loop with no Wait() — Code Smith flags those and other heavy patterns so you can fix them before they cause lag.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Code Smith scans a zipped FiveM resource and reports common scripting issues that cause
          server performance problems.
        </p>
        <p className="text-sm font-medium text-foreground">What it detects</p>
        <BulletList
          items={[
            "Tight loops with no Wait() call — the most common cause of server thread starvation.",
            "Heavy event handlers that execute expensive logic on every tick.",
            "Missing Citizens.CreateThread wrappers around async-pattern code.",
            "Unused asset references (models registered but never requested).",
            "Deprecated natives still present in the codebase.",
          ]}
        />
        <Callout type="warn">
          Code Smith is a static analyser — it cannot catch runtime bugs or logic errors, only
          known anti-patterns.
        </Callout>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Zip your FiveM resource folder (must include fxmanifest.lua at root).",
            "Drop the ZIP onto Code Smith.",
            "Review the report — each finding shows the file, line, and a description.",
            "Fix the flagged issues, re-zip, and re-scan to confirm.",
          ]}
        />
      </>
    ),
  },
  {
    id: "model-forge",
    label: "Model Forge",
    icon: <Sparkles className="h-5 w-5" />,
    shortDescription: "Generate game-ready 3D models from a text prompt or reference image.",
    badge: "AI",
    seoTitle: "AI 3D Model Generator for FiveM | Model Forge",
    seoDescription:
      "Generate game-ready 3D models from a text prompt or reference image with Model Forge. Powered by Tripo AI — export GLB with PBR textures for Blender, UV Studio, and FiveM.",
    keywords: [
      "AI 3D model generator",
      "FiveM 3D asset creation",
      "text-to-3D modeling",
      "image-to-3D reconstruction",
      "Tripo AI",
      "game-ready 3D models",
      "GLB export",
      "FiveM prop creation",
    ],
    toolHref: "/model-forge",
    faqs: [
      {
        q: "What output format does Model Forge produce, and is it FiveM-ready?",
        a: "GLB is the default output — a glTF binary with embedded PBR textures that opens in Blender, Maya, UV Studio, and Paint Booth. For FiveM, import the GLB into Blender to clean it up and convert to YDR. You can also download FBX or OBJ.",
      },
      {
        q: "Can image-to-3D reconstruct an existing prop or vehicle part?",
        a: "Yes. Upload a clear, well-lit photo or render with the object centered on a plain background, then add extra instructions to guide shape cleanup, scale, or materials for a better result.",
      },
      {
        q: "How long does a generation take, and can I close the tab?",
        a: "Most generations finish in 1–3 minutes. Stay on the page while it runs — the status and progress percentage update live, and closing the tab can interrupt the task.",
      },
      {
        q: "What happens if a generation fails?",
        a: "If Tripo reports a failure or you cancel, your 100 tokens are automatically refunded. An error is shown so you can refine the prompt or image and try again.",
      },
      {
        q: "Can I download a model before saving it to my library?",
        a: "Yes. Once a generation succeeds you can download it straight to your computer, or save it to your My Models library first — both options stay available.",
      },
      {
        q: "What polygon count should I use for FiveM?",
        a: "The slider runs from 1k to 100k faces (default 10k). Props are typically 5k–30k and vehicles 30k–80k+. You can always re-import into Blender and optimize with LODs afterward.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Model Forge turns a text prompt or a reference image into a game-ready 3D model using
          Tripo AI. Output is a <Tag>.glb</Tag> file with embedded PBR textures that drops straight
          into Blender, Maya, UV Studio, and Paint Booth — and converts cleanly to YDR for FiveM.
          Every model is polygon-customizable and saved to your personal library.
        </p>
        <Callout type="info">
          Each generation costs <strong>100 tokens</strong> and is auto-refunded if Tripo reports a
          failure or you cancel. Standard, Pro, and Max plans include monthly credits.
        </Callout>
        <p className="text-sm font-medium text-foreground">Two ways to generate</p>
        <BulletList
          items={[
            "Text-to-3D — describe the object in up to 1,000 characters, with an optional negative prompt to steer quality.",
            "Image-to-3D — upload a reference image (PNG, JPG, or WebP up to 20 MB) of a single object on a plain background, plus optional instructions for materials and scale.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Pick a mode: Text-to-3D or Image-to-3D.",
            "Enter your prompt (and optional negative prompt), or upload a reference image with extra instructions.",
            "Set the max polygon count with the slider (1k–100k, default 10k).",
            "Click Generate 3D Model — 100 tokens are reserved and generation runs for 1–3 minutes.",
            "Watch the live progress, then preview the result in the 3D viewer (toggle wireframe, reset camera).",
            "Save it to your My Models library, or download as GLB, FBX, or OBJ.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Export formats</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.glb</Tag>
          <Tag>.fbx</Tag>
          <Tag>.obj</Tag>
        </div>
        <Callout type="tip">
          Be specific in text prompts — <em>stainless steel toolbox with wear marks, 80cm wide,
          mounted on a truck bed</em> beats <em>toolbox</em>. For image-to-3D, a single centered
          object on a white or gray background gives the cleanest reconstruction.
        </Callout>
        <Callout type="warn">
          Model Forge requires the <Tag>tripoai</Tag> entitlement on your account. It is included
          with paid plans; free accounts need it granted explicitly.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Once you have a model, recover its UV layout in{" "}
          <a
            href="/guides/tools/uv-studio"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            UV Studio
          </a>{" "}
          and bundle it for your server with{" "}
          <a
            href="/guides/tools/packbench"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            PackBench
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "asset-art",
    label: "Asset Art",
    icon: <ImageIcon className="h-5 w-5" />,
    shortDescription: "Generate transparent inventory item icons with guided AI.",
    badge: "AI",
    seoTitle: "AI Inventory Icon Generator for FiveM | Asset Art",
    seoDescription:
      "Create transparent FiveM inventory item icons with AI. Describe an item, pick a style preset, generate 1024×1024 art, iterate with edits, and download inventory-ready PNGs.",
    keywords: [
      "FiveM inventory icons",
      "item icon generator AI",
      "transparent PNG inventory",
      "ox_inventory icon creator",
      "qb-inventory artwork",
      "roleplay item artwork",
    ],
    toolHref: "/item-image",
    faqs: [
      {
        q: "Can I generate clothing or vehicle icons with Asset Art?",
        a: "Asset Art is built for inventory item icons — weapons, medical, documents, food, tools, and so on. For vehicle liveries use Livery Magic, and for server or crew logos use Badge Maker.",
      },
      {
        q: "What size are the icons and do they have a transparent background?",
        a: "Every icon is generated at 1024×1024 with a fully transparent background and downloads at an inventory-ready 100×100, so it scales cleanly into your UI without quality loss.",
      },
      {
        q: "Can I tweak an icon after it's generated?",
        a: "Yes. Use Request Changes to describe exactly what to modify — colors, materials, added details — and the AI re-renders the same image with only those changes, preserving the style and composition.",
      },
      {
        q: "Which inventory systems is it compatible with?",
        a: "The transparent PNG output works with ox_inventory, qb-inventory, qs-inventory, lj-inventory, and most custom FiveM frameworks that accept PNG item icons.",
      },
      {
        q: "How many tokens does it cost?",
        a: "Generating an icon costs 100 tokens and each edit costs 40 tokens. Tokens are refunded if a generation fails, and your last 12 generations are saved to history for reloading.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Asset Art generates transparent-background inventory icons for FiveM roleplay servers.
          Describe an item, choose a style preset, and the AI produces a 1024×1024 PNG in about
          10–15 seconds. Refine it with plain-language edit requests, then download an
          inventory-ready 100×100 icon.
        </p>
        <Callout type="info">
          Output is 1024×1024 with a fully transparent background and downloads at 100×100 — drop it
          straight into <Tag>ox_inventory</Tag>, <Tag>qb-inventory</Tag>, and most custom frameworks.
        </Callout>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Enter the exact item name as it appears in your inventory system (e.g. Police Evidence Bag, Lockpick, Medkit).",
            "Pick a style preset that matches your server's look.",
            "Optionally add details — colors, materials, markings, branding.",
            "Click Generate item image to create a 1024×1024 transparent PNG.",
            "Use Apply changes to iterate — describe colors or details to add or change.",
            "Download the icon, or reload any of your last 12 generations from history to edit again.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Style presets</p>
        <BulletList
          items={[
            "Semi-Realistic, GTA V, Cartoon, and Minimal for general items.",
            "Luxury for premium goods, and Police, Medical, and Mechanic for department items.",
            "Food and Document presets for consumables and RP paperwork.",
          ]}
        />
        <Callout type="tip">
          Use your real item name — it sets the AI's context and becomes the filename. For details,
          write <em>gold badge with blue enamel, worn edges, courthouse seal</em> rather than just{" "}
          <em>badge</em>.
        </Callout>
        <Callout type="warn">
          Generating costs <strong>100 tokens</strong>; each edit costs <strong>40 tokens</strong>.
          Edits are applied to the existing image — to make a completely different item, start a
          fresh generation.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Need a crew logo or department crest instead of an item? Use{" "}
          <a
            href="/guides/tools/badge-maker"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Badge Maker
          </a>
          , which shares the same credit pool.
        </p>
      </>
    ),
  },
  {
    id: "badge-maker",
    label: "Badge Maker",
    icon: <Award className="h-5 w-5" />,
    shortDescription: "Generate badges, crests, and emblems for liveries and crews.",
    badge: "AI",
    seoTitle: "AI Badge, Crest & Emblem Maker for FiveM | Badge Maker",
    seoDescription:
      "Generate badges, crests, and emblems for your FiveM server, crew, or community. Transparent PNG output, 10+ visual styles, and iterative editing — built for Discord icons and liveries.",
    keywords: [
      "FiveM badge generator",
      "AI logo maker",
      "roleplay server emblem",
      "GTA5 crew crest",
      "Discord icon generator",
      "livery decal generator",
    ],
    toolHref: "/badge-maker",
    faqs: [
      {
        q: "What size are the badges and can I use them on Discord?",
        a: "Badges generate at 1024×1024 with a transparent background and scale cleanly down to 64×64, so they work as Discord server icons, livery decals, crew crests, and stream overlays.",
      },
      {
        q: "Can I edit a badge after generating it?",
        a: "Yes. Switch to Request Changes and describe the tweak — the AI preserves the core design, typography, and style while applying only what you ask. Each edit costs 40 tokens.",
      },
      {
        q: "How much does it cost?",
        a: "Generating a badge costs 100 tokens; each edit costs 40 tokens. Iterating with edits is much cheaper than regenerating from scratch.",
      },
      {
        q: "What styles are available?",
        a: "Ten presets: Modern/Flat, Minimalist, Retro/Vintage, Esports/Gaming, Luxury/Premium, Shield/Crest, Military/Tactical Patch, Neon/Cyberpunk, Grunge/Street Art, and Cartoon/Illustrated.",
      },
      {
        q: "Does Badge Maker share credits with Asset Art?",
        a: "Yes — Badge Maker draws from the same monthly credit pool as Asset Art, so your plan's credits cover both tools.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Badge Maker generates logos, badges, crests, and emblems for FiveM servers, gaming crews,
          and Discord communities. Describe your brand, pick a visual style, and the AI produces a
          1024×1024 transparent PNG you can refine with plain-language edits — ready for Discord
          icons, livery decals, and stream overlays.
        </p>
        <Callout type="info">
          Badges are optimized to stay legible at 64×64, so the same file works as a favicon, a
          crew patch, or a roof decal in a livery.
        </Callout>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Enter your server, crew, or brand name and pick a visual style.",
            "Optionally add design guidance — colors, symbols, mood, layout.",
            "Click Generate badge to create the initial 1024×1024 transparent PNG.",
            "Describe tweaks in Request Changes and click Apply changes to refine.",
            "Download the final badge, or reload any of your last 12 badges from history.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Style presets</p>
        <BulletList
          items={[
            "Modern/Flat, Minimalist, and Cartoon/Illustrated for clean, friendly marks.",
            "Shield/Crest and Military/Tactical Patch for departments and units.",
            "Esports/Gaming, Neon/Cyberpunk, Grunge/Street Art, Retro/Vintage, and Luxury/Premium for crews and brands.",
          ]}
        />
        <Callout type="tip">
          Keep the name short (3–4 words) for cleaner typography, and use edits to tweak colors or
          add elements rather than requesting a full redesign.
        </Callout>
        <Callout type="warn">
          Generating costs <strong>100 tokens</strong> and each edit costs <strong>40 tokens</strong>,
          drawn from the same credit pool as Asset Art.
        </Callout>
      </>
    ),
  },
  {
    id: "paint-booth-studio",
    label: "Paint Booth Studio",
    icon: <Brush className="h-5 w-5" />,
    shortDescription: "A layer-based texture editor docked to a live 3D vehicle preview.",
    seoTitle: "Layer-Based Livery Studio with Live 3D Preview | Paint Booth Studio",
    seoDescription:
      "Design FiveM vehicle liveries in a Photoshop-style canvas docked to a live 3D preview. Layers, PSD/DDS/PNG import, image adjustments, and YTD/PNG export — all in the browser.",
    keywords: [
      "FiveM texture editor",
      "vehicle livery studio",
      "layer-based livery editor",
      "PSD DDS PNG import",
      "YTD export tool",
      "real-time 3D texture preview",
    ],
    toolHref: "/paintbooth-studio",
    faqs: [
      {
        q: "How is Paint Booth Studio different from Paint Booth?",
        a: "Paint Booth is a quick live preview — drop a texture and see it on a 3D vehicle. Paint Booth Studio is a full layer-based editor: paint, mask, adjust, and organize layers on a canvas while the 3D preview updates in real time, then export the finished texture.",
      },
      {
        q: "What file formats can I import?",
        a: "PNG, JPG, WebP, DDS, PSD (Photoshop), XCF (GIMP), AI (Illustrator), and PDN (Paint.NET) for textures, plus YFT, GLB, and OBJ models for the live preview.",
      },
      {
        q: "Can I preview my edits on a vehicle in real time?",
        a: "Yes. Import a YFT, GLB, or OBJ model and the docked 3D preview updates instantly as you paint and adjust the texture.",
      },
      {
        q: "How do I export a texture for FiveM?",
        a: "Export a PNG (Cmd/Ctrl+E) for immediate use, or export a native GTA5 YTD — that requires power-of-two canvas dimensions (256, 512, 1024…) and a loaded model.",
      },
      {
        q: "Can I save a project and come back to it?",
        a: "Yes. Projects save locally in your browser with full layers and edit history. Free accounts can save up to 2 projects; paid plans are unlimited.",
      },
      {
        q: "What editing tools are included?",
        a: "Select, Move, Brush, Eraser, Fill, Text, Color Picker, Pen, and shapes, plus image adjustments like Levels, Hue/Saturation, Blur, and Sharpen — organized with a full Layers panel and layer masks.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Paint Booth Studio is a Photoshop-style, layer-based texture editor docked to a live 3D
          vehicle preview. Paint, mask, and adjust your livery on a canvas while the model updates in
          real time, then export a PNG or a native GTA5 YTD ready for your server.
        </p>
        <Callout type="info">
          Looking for a quick look instead of a full edit? Use{" "}
          <a
            href="/guides/tools/paint-booth"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Paint Booth
          </a>{" "}
          for a live drop-in preview. Studio adds layers, adjustments, and export.
        </Callout>
        <p className="text-sm font-medium text-foreground">Supported imports</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.png</Tag>
          <Tag>.jpg</Tag>
          <Tag>.webp</Tag>
          <Tag>.dds</Tag>
          <Tag>.psd</Tag>
          <Tag>.xcf</Tag>
          <Tag>.ai</Tag>
          <Tag>.pdn</Tag>
          <Tag>.yft</Tag>
          <Tag>.glb</Tag>
          <Tag>.obj</Tag>
        </div>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Open an existing texture, import from your library, or create a new blank canvas.",
            "Import a YFT, GLB, or OBJ model to dock the live 3D preview.",
            "Paint and edit with the left toolbar — brush, eraser, fill, text, shapes, and more.",
            "Organize the artwork with layers, groups, and masks in the Layers panel.",
            "Apply image adjustments — Levels, Hue/Saturation, Blur, Sharpen.",
            "Save the project locally (Cmd/Ctrl+S), then export a PNG or YTD.",
          ]}
        />
        <Callout type="tip">
          Tools have one-key shortcuts — B for Brush, E for Eraser, T for Text, V for Move, Z for
          Zoom. Drop a texture onto an empty workspace to open it as the canvas; further drops add
          new layers.
        </Callout>
        <Callout type="warn">
          YTD export needs power-of-two dimensions (256, 512, 1024, …) and a loaded model. Resize via
          Image → Image Size first if your canvas doesn't match.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Free accounts can save up to 2 Studio projects locally; paid subscribers get unlimited
          saves.
        </p>
      </>
    ),
  },
  {
    id: "addon-eup-creator",
    label: "Add-on EUP Creator",
    icon: <UserPlus className="h-5 w-5" />,
    shortDescription: "Scaffold a streaming-ready EUP add-on resource from clothing files.",
    seoTitle: "Add-on EUP Resource Creator for FiveM | The Workshop",
    seoDescription:
      "Convert EUP clothing files between FiveM and single-player layouts. Scaffold a streaming-ready add-on resource with auto-generated fxmanifest.lua and folder structure — no local tools.",
    keywords: [
      "EUP creator FiveM",
      "EUP add-on resource",
      "clothing resource builder",
      "ydd ytd converter",
      "fxmanifest generator",
      "streaming-ready clothing",
    ],
    toolHref: "/addon-eup-creator",
    faqs: [
      {
        q: "What files does the Add-on EUP Creator accept?",
        a: "YDD drawables, YTD and DDS textures, PNG/JPG images, and YMT/XML metadata. Upload individual files or drag in a whole folder — it filters by extension and skips hidden files.",
      },
      {
        q: "How does gender detection work?",
        a: "It reads filenames for mp_m_ (male) or mp_f_ (female). If neither marker is present it defaults to female, so double-check the detected peds before you build.",
      },
      {
        q: "Can I convert between single-player and FiveM layouts?",
        a: "Yes. Pick SP-to-addon to go from single-player to streaming-ready FiveM, or addon-to-SP to reverse it — five conversion modes are available in the Mode dropdown.",
      },
      {
        q: "Do I need to supply metadata files?",
        a: "For add-on conversions, include your existing .ymt and the tool organizes it into the right place. It doesn't invent metadata from scratch — finish any new .ymt in CodeWalker or YMTEditor.",
      },
      {
        q: "What do I get when I build the ZIP?",
        a: "A reorganized resource in the target layout with a generated fxmanifest.lua (for add-on modes), a README of conversion notes, and a manifest listing every output path — review the paths before downloading.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Add-on EUP Creator scaffolds streaming-ready EUP add-on resources from raw clothing files.
          It organizes drawables, textures, and metadata into the correct folder structure,
          generates <Tag>fxmanifest.lua</Tag>, and exports everything as a ready-to-drop ZIP —
          converting between single-player and FiveM layouts in either direction.
        </p>
        <Callout type="info">
          Five conversion modes cover SP → non-addon, non-addon → addon, SP → addon, and the two
          reverse flows for getting clothing back into single-player.
        </Callout>
        <p className="text-sm font-medium text-foreground">Accepted files</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.ydd</Tag>
          <Tag>.ytd</Tag>
          <Tag>.ymt</Tag>
          <Tag>.dds</Tag>
          <Tag>.png</Tag>
          <Tag>.meta</Tag>
          <Tag>.xml</Tag>
        </div>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Pick a conversion mode from the Mode dropdown.",
            "Enter a pack name — it's auto-slugified for the resource name.",
            "Upload clothing files individually or drag in an entire folder.",
            "Review the auto-detected gender and assets grouped by component slot.",
            "Click Build ZIP to generate the resource structure.",
            "Check the output paths, then download the ZIP.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What it generates</p>
        <BulletList
          items={[
            "A correctly-structured stream/ or dlc.rpf/ folder for the target layout.",
            "fxmanifest.lua for add-on FiveM conversions.",
            "A README with conversion notes and a manifest of every output path.",
          ]}
        />
        <Callout type="tip">
          Gender is detected from filenames (mp_m_ = male, mp_f_ = female); files without those
          markers default to female. Re-uploading the same file is safe — duplicates are removed by
          path and size.
        </Callout>
        <Callout type="warn">
          The tool organizes existing <Tag>.ymt</Tag> metadata rather than inventing it. Include
          your source pack's .ymt for add-on conversions, and verify single-player DLC metadata in
          CodeWalker or YMTEditor before installing.
        </Callout>
      </>
    ),
  },
  {
    id: "embroidery-tool",
    label: "Embroidery Tool",
    icon: <Scissors className="h-5 w-5" />,
    shortDescription: "Turn artwork into a stitched patch-style transparent PNG with AI.",
    badge: "AI",
    seoTitle: "AI Embroidered Patch Maker for FiveM | Embroidery Tool",
    seoDescription:
      "Convert FiveM artwork into an embroidered patch-style transparent PNG. Upload a logo, add thread notes, generate a stitched PNG, then download or publish.",
    keywords: [
      "FiveM embroidered patch generator",
      "FiveM police shoulder patch PNG",
      "AI embroidery effect for logos",
      "transparent patch PNG maker",
      "FiveM EUP patch texture",
      "stitched logo generator",
      "BCSO LSPD patch maker",
    ],
    toolHref: "/embroidery-tool",
    faqs: [
      {
        q: "How many tokens does it cost?",
        a: "Each run costs 40 tokens, spent before the AI call and auto-refunded if the provider fails or returns no image. The Embroider image button is disabled whenever your balance is below 40, and an empty balance returns a 'Not enough tokens for this generation' message.",
      },
      {
        q: "What can I upload, and what do I get back?",
        a: "Upload PNG, JPG, or WEBP artwork — any non-image file is rejected. You get back a transparent-background PNG, requested at 1024×1024 (1:1). Your source image is capped at 1024px before it is sent.",
      },
      {
        q: "How is this different from Badge Maker?",
        a: "Badge Maker designs a logo, crest, or emblem from a text prompt. Embroidery Tool takes finished artwork and applies the embroidery effect — stitched borders, thread texture, and a woven weave. They share the same generation backend, the 40-token cost, and your token balance.",
      },
      {
        q: "How does it make the background transparent?",
        a: "Transparency is applied in your browser, not by the model. A border-seeded flood fill deletes background-colored pixels, so subjects that match the detected border color can be partially erased, and disconnected interior background pockets are not removed.",
      },
      {
        q: "Do I need a separate plan from Badge Maker?",
        a: "No. Embroidery Tool uses the same AI image access and the same token balance as Asset Art and Badge Maker — if you can generate badges, you can generate embroidery.",
      },
      {
        q: "Does it save my results?",
        a: "Yes — each success saves to a dedicated embroidery history of up to 12 items, with relative time and size. You can reload, download, or remove individual tiles, or clear them all. Export anything you want to keep, since history is capped at 12.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Embroidery Tool takes a logo or badge and renders it as an embroidered patch — stitched
          thread borders, thread texture, and a woven fabric weave — while keeping the original
          design, symbols, colors, and typography intact. Upload a PNG, JPG, or WEBP, add optional
          thread notes, and download a transparent-background PNG ready to drop onto a uniform or
          publish to the Showcase.
        </p>
        <Callout type="info">
          The result is a transparent-background PNG, requested at 1024×1024 (1:1). Transparency is
          applied in the browser with a border-seeded flood fill, so the patch drops cleanly onto any
          background.
        </Callout>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Click Upload image and pick a PNG, JPG, or WEBP — the Name field auto-fills from the filename.",
            "Optionally edit the name (up to 200 characters) and add thread instructions (up to 500) — thread color, a merrowed edge, or keep text sharp.",
            "Click Embroider image. The button is disabled until an image is loaded and whenever your balance is under 40 tokens.",
            "Wait for the result to render in the Embroidered panel as the AI adds stitched borders, thread texture, and a woven weave.",
            "Download the transparent PNG, or Share to Showcase once it is saved.",
            "Reload, download, or remove any of your last 12 results from History.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What the AI adds</p>
        <BulletList
          items={[
            "Stitched thread borders around the design edges",
            "Thread texture across the filled areas",
            "A woven fabric weave under the artwork",
            "Original symbols, colors, and typography preserved",
            "Background flood-filled to transparent in the browser",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Export format</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.png</Tag>
        </div>
        <Callout type="tip">
          Use the instructions field to steer the result — name a thread color, ask for a merrowed
          edge, tell it to keep text sharp, or to simplify tiny details that won't survive as
          stitching.
        </Callout>
        <Callout type="warn">
          Generating costs <strong>40 tokens</strong>, drawn from your global balance (shared with
          Asset Art and Badge Maker). Tokens are spent up front and refunded automatically if a
          generation fails or returns no image.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Designing the badge itself, or making inventory icons?{" "}
          <a
            href="/guides/tools/badge-maker"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Badge Maker
          </a>{" "}
          generates crests and emblems from a prompt, and{" "}
          <a
            href="/guides/tools/asset-art"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Asset Art
          </a>{" "}
          covers transparent item icons — both share the same token balance.
        </p>
      </>
    ),
  },
  {
    id: "creator-convert",
    label: "Creator Convert",
    icon: <ImageDown className="h-5 w-5" />,
    shortDescription: "Convert DDS, images, and YTD dictionaries in the browser — no upload.",
    seoTitle: "FiveM DDS Converter & YTD Extractor | Creator Convert",
    seoDescription:
      "Decode DDS to PNG/WebP/JPEG, encode images to DDS with mipmaps, and extract YTD dictionaries to a ZIP — all in your browser, no upload, no credit cost.",
    keywords: [
      "FiveM DDS converter",
      "DDS to PNG",
      "PNG to DDS",
      "YTD to PNG extractor",
      "GTA5 texture converter",
      "convert DDS online",
      "YTD texture dictionary extract",
      "DDS mipmaps generator",
    ],
    toolHref: "/creator-convert",
    faqs: [
      {
        q: "How much does Creator Convert cost to run?",
        a: "Nothing per conversion. DDS decode and encode, YTD parsing, canvas encoding, and ZIP creation all run locally in your browser — there is no API route, server action, or token cost. Access to the tool is included with Standard plans and above.",
      },
      {
        q: "Why does my compressed DDS look blocky or washed out?",
        a: "The browser-side compressor is fast but lossy — it writes a single averaged color per 4×4 block for every compressed format (DXT1/3/5, BC4, BC5, BC7), with no endpoint optimization. For pixel-exact output, choose 32-bit RGBA instead; it preserves pixels exactly at the cost of a larger, uncompressed file.",
      },
      {
        q: "Which DDS formats can it read versus write?",
        a: "Decoding is broader than encoding. DDS to image decodes DXT1/3/5, BC4, BC5, BC7, uncompressed 32-bit RGBA, and DX10-header BC1/2/3/4/5/7. Image to DDS writes DXT1/3/5, BC4, BC5, BC7, or 32-bit RGBA — but every compressed encode uses the flat-per-block method. Unsupported DDS formats throw an error on decode.",
      },
      {
        q: "Why are some textures missing from my YTD ZIP?",
        a: "YTD extraction only decodes textures stored as A8R8G8B8, X8R8G8B8, DXT1, or DXT5, and reads only the first mip level. Other formats inside the dictionary are counted as unsupported and skipped. If none are decodable you will see 'No supported textures were found in this YTD'.",
      },
      {
        q: "Can I convert several files at once?",
        a: "No. The dropzone handles one file at a time — the first one dropped or selected, with extras ignored. The mode also has to match the file type, so DDS to image will not accept a PNG and Image to DDS will not accept a .dds.",
      },
      {
        q: "Does my PNG keep its transparency?",
        a: "It depends on the output. PNG and WebP both preserve the alpha channel (WebP encodes at quality 0.92). JPEG flattens onto a white background and drops alpha, so use PNG or WebP for any texture that needs transparency.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Creator Convert turns FiveM and GTA5 textures into the format you actually need, entirely
          in your browser. Decode a <Tag>.dds</Tag> straight to PNG, WebP, or JPEG; encode an image
          back into a <Tag>.dds</Tag> with DXT1/3/5, BC4/5/7, or uncompressed 32-bit RGBA and
          optional mipmaps; or open a <Tag>.ytd</Tag> texture dictionary and pull its embedded
          textures out as PNGs in a single ZIP.
        </p>
        <Callout type="info">
          Every conversion runs client-side — DDS decode and encode, YTD parsing, and ZIP packaging
          never hit a server. There is no per-conversion token or credit cost, and your files never
          leave your machine.
        </Callout>
        <p className="text-sm font-medium text-foreground">Accepted files</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.dds</Tag>
          <Tag>.png</Tag>
          <Tag>.jpg</Tag>
          <Tag>.webp</Tag>
          <Tag>.bmp</Tag>
          <Tag>.gif</Tag>
          <Tag>.ytd</Tag>
        </div>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Pick a mode in the Convert panel: DDS → image, Image → DDS, or YTD → ZIP. Switching modes clears the current file.",
            "Choose the output format — PNG, WebP, or JPEG for DDS → image; DXT1/3/5, BC4/5/7, or 32-bit RGBA for Image → DDS (with an optional mipmap chain); YTD → ZIP always outputs PNGs.",
            "Drag a file onto the dropzone or click Choose — conversion runs automatically the moment it lands.",
            "For DDS and image conversions, preview the result with Source, Output, Size, and File size, then click Download.",
            "For YTD → ZIP, page through the extracted textures, tick the ones to keep (or Select all / none), and click Download N PNGs.",
            "Switch the output format to re-convert the same file instantly, or click Convert another / Clear to start over.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Three conversion modes</p>
        <BulletList
          items={[
            "DDS → image: decodes a .dds and re-encodes as PNG, WebP, or JPEG. Decoding covers DXT1/3/5, BC4, BC5, BC7, uncompressed 32-bit RGBA, and DX10-header BC1/2/3/4/5/7.",
            "Image → DDS: encodes a PNG, JPEG, WebP, BMP, or GIF into a .dds as DXT1/3/5, BC4, BC5, BC7, or uncompressed 32-bit RGBA, with an optional full mipmap chain.",
            "YTD → ZIP: parses an RSC7 .ytd dictionary and extracts supported textures as PNGs, letting you pick which to keep before downloading them in one ZIP.",
            "PNG and WebP preserve alpha; JPEG flattens onto white and drops the alpha channel.",
            "Mipmaps (Image → DDS) box-downsample each level to 1×1 for in-game texture streaming — on by default.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Export formats</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.png</Tag>
          <Tag>.webp</Tag>
          <Tag>.jpg</Tag>
          <Tag>.dds</Tag>
          <Tag>.zip</Tag>
        </div>
        <Callout type="tip">
          Re-converting is automatic — once a result loads, switching the output format (or toggling
          mipmaps in Image → DDS) re-runs the conversion on the same file, with no re-import needed.
        </Callout>
        <Callout type="warn">
          Compressed DDS encoding is fast but lossy — every encoder writes one averaged color per 4×4
          block, so DXT1/3/5, BC4, BC5, and BC7 output is low quality. Only 32-bit RGBA preserves
          pixels exactly. Creator Convert is included with Standard plans and above.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Creator Convert handles the format plumbing — pair it with the studios where you paint and
          lay out those textures.{" "}
          <a
            href="/guides/tools/uv-studio"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            UV Studio
          </a>{" "}
          recovers and previews the UV layout, and{" "}
          <a
            href="/guides/tools/paint-booth-studio"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Paint Booth Studio
          </a>{" "}
          is the full vehicle texture editor.
        </p>
      </>
    ),
  },
  {
    id: "chevron-builder",
    label: "Chevron Builder",
    icon: <AlertTriangle className="h-5 w-5" />,
    shortDescription: "Design rear warning chevron livery patterns and export a flat PNG, in-browser.",
    seoTitle: "FiveM Chevron Livery Generator | Chevron Builder",
    seoDescription:
      "Build rear warning chevron and hazard stripe patterns for FiveM emergency liveries. Tune colors, angle, ratio, reflective and honeycomb overlays, add text, export PNG.",
    keywords: [
      "FiveM chevron livery",
      "rear chevron generator",
      "emergency vehicle warning stripes",
      "FiveM hazard stripe pattern",
      "fire EMS chevron texture",
      "reflective conspicuity stripes",
      "diagonal warning stripe maker",
    ],
    toolHref: "/chevron-builder",
    faqs: [
      {
        q: "How many tokens does it cost to use?",
        a: "None. Chevron Builder is fully client-side — the pattern is drawn on an HTML5 canvas and exported in the browser, so there is no AI call, no server action, and no token or credit deduction. The only requirement is a plan that includes it.",
      },
      {
        q: "What format does it export, and at what resolution?",
        a: "A single flat PNG at exactly the canvas Size you set (Width min 200, Height min 100, default 800×500). There is no SVG, layers, or transparency control — it is a flat raster. Zoom is view-only and does not change the exported size.",
      },
      {
        q: "Is the Stripe Width a real measurement in inches?",
        a: "No. Width is expressed in inches relative to a fixed 96-inch-wide virtual canvas, so the same inch value produces different pixel widths at different canvas widths. Treat it as a proportional control, not a true physical dimension.",
      },
      {
        q: "What ends up in the exported PNG?",
        a: "The stripes, overlays, seam line (if shown), text overlay, and the dark vehicle-body background. The faint UV reference grid, corner alignment marks, and the canvas-edge indicator are editor guides only and are not included.",
      },
      {
        q: "Can I save a design or share a link to it?",
        a: "Not as an editable design. There is no save/load and no link that reconstructs your settings — reloading the page resets everything to the Fire Standard default. The Share button captures the current canvas as an image and posts it to the community Showcase, but it does not store your control values. Export your PNG before you leave.",
      },
      {
        q: "How is the text overlay limited?",
        a: "Text is always uppercase with a fixed white fill and black stroke (no font or color choice), capped at 40 characters, and stays upright even when you rotate the pattern. You position it by dragging on the canvas — there are no numeric X/Y fields.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Chevron Builder generates rear warning chevron and hazard-stripe patterns for FiveM
          emergency-vehicle liveries entirely in your browser. Pick two stripe colors, dial in angle,
          width, rotation, and ratio, then layer optional honeycomb or 3M-983-style reflective effects
          and a draggable text overlay. The canvas redraws in real time as you tune it, and one click
          exports a flat PNG at whatever resolution you set.
        </p>
        <Callout type="info">
          Everything runs client-side on an HTML5 canvas and exports straight from the canvas — no
          upload, no server call, no AI, and zero token or credit cost. Access is a subscription
          entitlement, not a per-use charge.
        </Callout>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Optionally start from one of six presets — Fire Standard, EMS White, DOT Highway, Police Vis, Tow Warning, or Rescue Red. Each one overwrites the current settings.",
            "Set Primary and Secondary stripe colors from the six swatches or the custom color picker.",
            "Tune the geometry: Stripe Width (2–16 in), Angle (20–75°), Rotation (0–360°), and Ratio (0.3–3.0).",
            "Layer effects: Mirror Over Seam, Show Seam Line, Honeycomb Overlay, and 3M 983 Reflective Conspicuity — each with its own opacity slider.",
            "Add a text overlay from a preset (KEEP BACK, STAY BACK 500 FT, EMERGENCY…) or type your own up to 40 characters, then drag it into place on the canvas.",
            "Set the output Size (min 200×100) and use Zoom or Fit to inspect — zoom is view-only and does not change the export.",
            "Click Export PNG to download chevrons_<angle>deg.png, or Reset to return to the Fire Standard default.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Starter presets</p>
        <BulletList
          items={[
            "Fire Standard — red and yellow, 45°, mirrored",
            "EMS White — orange and white, mirrored",
            "DOT Highway — black and yellow hazard",
            "Police Vis — blue and silver, mirrored, ratio 1.2",
            "Tow Warning — amber and black, full rear",
            "Rescue Red — red and white, mirrored, angle 50, ratio 0.8",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Export format</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.png</Tag>
        </div>
        <Callout type="tip">
          Use Mirror Over Seam to make the chevrons point symmetrically inward or outward about the
          vertical center, and add the Honeycomb or 3M 983 overlay for a reflective-tape look before
          exporting.
        </Callout>
        <Callout type="warn">
          Chevron Builder is included with the Vehicle Developer, Pro, and Studio plans. Signed-out
          visitors are sent to sign in; subscribers without it land on the access screen.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Chevron Builder covers the rear warning panel — pair it with the other vehicle tools for
          full-livery work. Use{" "}
          <a
            href="/guides/tools/uv-studio"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            UV Studio
          </a>{" "}
          to work against the vehicle UV layout, and{" "}
          <a
            href="/guides/tools/paint-booth-studio"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Paint Booth Studio
          </a>{" "}
          for the broader paint and livery pipeline.
        </p>
      </>
    ),
  },
  {
    id: "code-visualizer",
    label: "Code Visualizer",
    icon: <Workflow className="h-5 w-5" />,
    shortDescription: "Map a FiveM resource into a draggable node graph of its events and exports.",
    seoTitle: "FiveM Resource Code Map & Graph | Code Visualizer",
    seoDescription:
      "Upload a FiveM resource and Code Visualizer parses its Lua, JS, and manifest into a zoomable node graph of commands, events, exports, and triggers. Runs in-browser.",
    keywords: [
      "FiveM resource code map",
      "visualize Lua code FiveM",
      "FiveM event trigger graph",
      "fxmanifest visualizer",
      "FiveM code analyzer",
      "Lua dependency graph FiveM",
      "read FiveM resource structure",
    ],
    toolHref: "/code-visualizer",
    faqs: [
      {
        q: "How many tokens does it cost?",
        a: "None. Parsing, graph layout, ZIP unpacking, the Monaco editor, syntax checking, and script export all run locally in your browser with no token or credit cost. The only server call the UI makes is saving a workspace to My Projects, which is also free.",
      },
      {
        q: "What can I upload, and does it read a whole resource at once?",
        a: "Upload loose files, a full resource folder, or a ZIP. It parses .lua, .js, .ts, .json, .cfg, .xml, .meta, and .txt; ZIPs are unpacked and filtered the same way. Anything else — images, models, audio — is skipped, so the graph only covers scriptable files.",
      },
      {
        q: "Why aren't some of my events linking to their handlers?",
        a: "Linking is an exact, case-insensitive match on the literal event name. Events triggered through a variable or config constant (like TriggerServerEvent(Config.SaveEvent)) cannot be resolved, so they will not connect. Also, a manifest named anything other than fxmanifest.lua or __resource.lua is parsed as ordinary Lua, not as a manifest.",
      },
      {
        q: "Are the dashed 'next' arrows real call relationships?",
        a: "No. When a file's visible nodes have no real links among them, the graph fabricates sequential 'next' edges chaining the first ~10 nodes in line order as layout filler. Treat the solid trigger-to-event and manifest-to-file links as the real wiring — the filler only appears when there are no real edges to draw.",
      },
      {
        q: "Can I edit code here, and will my project change?",
        a: "You can edit any file in the built-in Monaco (VS Code) editor with FiveM Lua highlighting and live error checking, then click Save to map to re-parse it or Export script to download it. Nothing touches your real resource files — edits stay in the tool until you export or click Save workspace, which writes the sources and layout to My Projects.",
      },
      {
        q: "How is this different from Code Smith?",
        a: "Code Visualizer maps a resource so you can see how it is wired — the events, commands, exports, and how they connect. Code Smith scans a resource for performance, security, and manifest risks and reports issues to fix. Use Code Visualizer to understand the structure, and Code Smith to harden it.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Code Visualizer turns a FiveM resource into a node graph you can actually read. Upload a
          folder, ZIP, or loose Lua/JS/TS/manifest files and it parses every script in your browser
          to surface commands, events, triggers, exports, callbacks, threads, dependencies, and
          config — then draws them as draggable, zoomable blocks. It links each trigger to its
          registered handler by name and ties manifest directives to the files they load, so you can
          see how an unfamiliar resource is wired before touching a line.
        </p>
        <Callout type="info">
          All parsing, graph layout, ZIP unpacking, and editing happen in your browser. The only time
          anything leaves your machine is when you click Save workspace, which writes the source files
          and map layout to My Projects. Nothing about the tool spends tokens or credits.
        </Callout>
        <p className="text-sm font-medium text-foreground">Accepted files</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.lua</Tag>
          <Tag>.js</Tag>
          <Tag>.ts</Tag>
          <Tag>.json</Tag>
          <Tag>.cfg</Tag>
          <Tag>.xml</Tag>
          <Tag>.meta</Tag>
          <Tag>.txt</Tag>
          <Tag>.zip</Tag>
        </div>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Load your code one of four ways: Upload files, Upload resource folder, Open a saved workspace, or Load demo script.",
            "The tool reads everything in-browser, unpacks any ZIP, runs the analyzer, and reports 'Mapped N nodes from M files' with header counts for Files, Commands, Events, Functions, and Exports.",
            "Read the graph in Visual map mode — click a node for its kind, label, line, and path; drag to reposition; pan the canvas; Ctrl+scroll or pinch to zoom.",
            "Filter with Search nodes (by kind, label, detail, path, or args) and toggle Hide unconnected to drop blocks with no edges.",
            "Click a node's pencil icon (or the Code editor tab) to open that file in the Monaco editor at the exact line, with FiveM Lua highlighting and live error markers.",
            "Click Save to map to re-parse your edits, or Export script to download the active file.",
            "Click Save workspace to persist the sources and layout to My Projects — you are prompted before leaving with unsaved edits.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What it detects</p>
        <BulletList
          items={[
            "Manifest directives from fxmanifest.lua / __resource.lua, linked to the files they load",
            "Commands registered with RegisterCommand (in Lua, the handler function must be on the same line)",
            "Events and net events, plus the triggers that fire them — linked by exact name match",
            "Exports and callbacks, including QBCore / ESX / ox_lib callback patterns",
            "Threads (CreateThread / Citizen.CreateThread / setTick) and manifest dependencies",
            "Config and data files (.json, .cfg, .xml, .meta), plus top-level JS/TS functions and arrow-function constants",
          ]}
        />
        <Callout type="tip">
          Trigger-to-event links only resolve on string-literal names. The demo resource fires events
          through Config.* variables on purpose, so those won't link — swap a dynamic name for a
          literal if you want a node to connect.
        </Callout>
        <Callout type="warn">
          Code Visualizer requires the Code Visualizer entitlement, included with the Server Developer
          plan and above. Analysis is regex- and line-based, not a full parser, so unusual formatting
          or multi-line declarations can be missed.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Code Visualizer is for reading and mapping a resource you already have. To scan that
          resource for performance, security, and manifest risks, use{" "}
          <a
            href="/guides/tools/code-smith"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Code Smith
          </a>
          , and when you are packaging a resource for release,{" "}
          <a
            href="/guides/tools/packbench"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            PackBench
          </a>{" "}
          handles the build.
        </p>
      </>
    ),
  },
  {
    id: "script-docs",
    label: "Script Docs",
    icon: <BookOpenText className="h-5 w-5" />,
    shortDescription: "Turn a FiveM resource ZIP into a README in your browser — no AI.",
    seoTitle: "FiveM Resource README Generator | Script Docs",
    seoDescription:
      "Drop a FiveM resource ZIP or script file and Script Docs scans commands, events, exports, dependencies, and config to generate a README.md you can export. Runs locally.",
    keywords: [
      "FiveM resource documentation generator",
      "FiveM README generator",
      "document FiveM resource commands and exports",
      "fxmanifest parser",
      "FiveM script docs tool",
      "generate README from Lua resource",
      "scan FiveM events and exports",
    ],
    toolHref: "/script-docs",
    faqs: [
      {
        q: "Does Script Docs cost tokens or credits?",
        a: "No. Generation and export run entirely in your browser — there is no API call, server action, or billing. Uploading, scanning, and downloading the README are all free and local.",
      },
      {
        q: "What does it output, and can I copy instead of download?",
        a: "A single README.md Markdown file. You preview the full text on the page and click Export Markdown to download a .md file named after the resource with a -README.md suffix. There is no copy-to-clipboard button and no other export format.",
      },
      {
        q: "Can I upload a whole resource, or one file at a time?",
        a: "Both. Drop a single supported script, manifest, or config file, or zip the resource folder and upload the .zip — the browser unzips it locally and scans every supported text file together. Non-ZIP uploads are one file at a time, so a ZIP is the way to document a full resource at once.",
      },
      {
        q: "Which files are read from a ZIP?",
        a: "Only supported text files: .lua, .js, .mjs, .cjs, .ts, .tsx, .cs, .json, .jsonc, .yaml, .yml, .toml, .cfg, .ini, and .md. Other files, including .xml and .meta, are ignored. The ZIP parser also handles only stored and deflate-compressed entries; other compression methods are skipped.",
      },
      {
        q: "Why are some commands or events missing from the output?",
        a: "Detection is regex- and line-based after stripping single-line Lua comments, so it can miss block comments, multi-line statements, or unusual formatting. Config files are recognized by name, and dynamic command names only resolve when the constant is found in the scanned Lua. The side panel also caps each Detected Interfaces list at 8 entries, though the full set still appears in the README and counters.",
      },
      {
        q: "Which plans include Script Docs, and is my output saved?",
        a: "It is included with the Server Developer, Pro, and Studio plans. There is no server-side save — export the README to keep it, since closing the page clears the doc.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Script Docs reads a FiveM resource and writes its README for you. Drop a resource ZIP or a
          single script, manifest, or config file, and the browser unzips it locally and scans every
          supported text file for commands, registered and triggered events, Lua and JS exports,
          export-call dependencies, fxmanifest metadata, and config files. You get four summary
          counters, a Detected Interfaces panel, and a live README.md preview, then export the
          Markdown with one click.
        </p>
        <Callout type="info">
          Everything runs client-side — your resource is unzipped and parsed in the browser with regex
          scanners. Nothing is sent to a server, and no tokens or credits are charged.
        </Callout>
        <p className="text-sm font-medium text-foreground">Accepted files</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.zip</Tag>
          <Tag>.lua</Tag>
          <Tag>.js</Tag>
          <Tag>.ts</Tag>
          <Tag>.cs</Tag>
          <Tag>.json</Tag>
          <Tag>.yaml</Tag>
          <Tag>.toml</Tag>
          <Tag>.cfg</Tag>
          <Tag>.ini</Tag>
          <Tag>.md</Tag>
        </div>
        <p className="text-sm font-medium text-foreground">Workflow</p>
        <StepList
          steps={[
            "Drop a file on the dropzone or use the file input — it accepts a resource .zip or a single supported script/manifest/config file. A built-in Demo Dispatch Utility loads by default.",
            "A ZIP is unzipped locally, filtered to supported text files, and decoded as UTF-8; a single file is read directly.",
            "Review the four counters: Commands, Events (registered + triggered), Exports, and Config.",
            "Check the Resource Summary — name, scanned file count, and script/manifest/config badges — and the Detected Interfaces panel.",
            "Read the live README.md preview.",
            "Click Export Markdown to download <resource>-README.md.",
          ]}
        />
        <p className="text-sm font-medium text-foreground">What it scans</p>
        <BulletList
          items={[
            "Commands from RegisterCommand, ESX.RegisterCommand, QBCore.Commands.Add, lib.addCommand, and RegisterKeyMapping",
            "Registered events from RegisterNetEvent, RegisterServerEvent, and AddEventHandler",
            "Triggered events from TriggerServerEvent, TriggerClientEvent, TriggerEvent, and the latent variants",
            "Lua exports and JS exports",
            "Export-call dependencies — which resources your code calls into",
            "fxmanifest.lua / __resource.lua metadata (fx_version, game, author, version), declared dependencies, and client/server/shared script lists",
            "Config files detected by name (config, settings, shared_config, server_config, client_config, or cfg)",
          ]}
        />
        <p className="text-sm font-medium text-foreground">Export format</p>
        <div className="flex flex-wrap gap-2">
          <Tag>.md</Tag>
        </div>
        <Callout type="tip">
          To document a whole resource at once, zip the folder before uploading — non-ZIP uploads are
          one file at a time, while a single ZIP scans every supported file together.
        </Callout>
        <Callout type="warn">
          Detection is regex- and line-based, not a full parser. It strips only single-line Lua
          comments, so block comments, multi-line statements, or unusual formatting can be missed, and
          dynamic command names only resolve when the constant is found in the scanned Lua. Treat the
          output as a strong starting README, not a guaranteed-complete reference.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          Script Docs sits alongside The Workshop's other code tools. Pair it with{" "}
          <a
            href="/guides/tools/packbench"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            PackBench
          </a>{" "}
          when you are bundling a resource for release, or{" "}
          <a
            href="/guides/tools/code-smith"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Code Smith
          </a>{" "}
          to scan the Lua it documents for performance and security issues.
        </p>
      </>
    ),
  },
];
